import React, { useState } from 'react';
import axios from 'axios';
import {
    EditorState, ContentState,
    convertToRaw,
    AtomicBlockUtils,
    CharacterMetadata,
    EditorBlock,
    RichUtils, SelectionState, genKey, ContentBlock,
} from 'draft-js';
import { List, Repeat } from 'immutable';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Configurations } from '../../Utils/Configurations';
import ImagesUpload from '../Images-Upload/Images-Upload';
import InsertVideo from '../Insert-Video/Insert-Video';
import InsertCarrossel from '../Insert-Carrossel/Insert-Carrossel';
import InsertInstagram, { modos as modosInstagram } from '../Insert-Instagram/Insert-Instagram';
import InsertPlaylist from '../Insert-Playlist/Insert-Playlist';
import InsertPodcast from '../Insert-Podcast/Insert-Podcast';
import { stringToSlug } from '../../Utils/StringToSlug';

/*
 * Extraído de Containers/Content/Article-Details.js (blocos customizados, blockRendererFn,
 * toolbar e a codificação/decodificação dos embeds). Article-Details.js não foi alterado; este
 * componente é hoje consumido apenas por Unlisted-Article-Details.js. Reaproveita, sem alterações,
 * os pickers de mídia já existentes (Insert-Video/Insert-Podcast/Insert-Carrossel/Insert-Instagram/
 * Insert-Playlist), que continuam consultando as APIs de vídeos/podcasts/playlists já em produção —
 * isso é anterior a esta tarefa e não faz parte do escopo "sem chamada de API" do conteúdo não listado.
 */

function removeEspacosHTML(strHTML) {
    if (strHTML === null || strHTML === '') return '';
    return strHTML.toString().replace(/<[^>]*>/g, '').replace(/(\r\n|\n|\r)/gm, '').replace(/&nbsp;/g, '');
}

function replaceAll(string, search, replace) {
    return string.split(search).join(replace);
}

function contadorTexto(strHTML) {
    if (strHTML === null || strHTML === '') return { caracteres: 0, palavras: 0 };
    const caracteres = strHTML.replace(/<[^>]*>/g, '').replace(/(\r\n|\n|\r)/gm, '').replace(/&nbsp;/g, '');
    const palavras = strHTML.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').split(/\s/g).filter(item => item !== '');
    return { caracteres: caracteres.length, palavras: palavras.length };
}

function escapaAtributo(valor) {
    return (valor === null || valor === undefined ? '' : valor.toString())
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function desescapaAtributo(valor) {
    return (valor || '')
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function extraiAtributos(tagHtml) {
    const attrs = {};
    const attrRegex = /([\w-]+)\s*=\s*"([^"]*)"/g;
    let m;
    while ((m = attrRegex.exec(tagHtml)) !== null) attrs[m[1]] = m[2];
    return attrs;
}

function montaAtributosEmbed(dados) {
    const partes = [
        `url="${escapaAtributo(dados.url)}"`,
        `title="${escapaAtributo(dados.title)}"`,
        `program="${escapaAtributo(dados.program)}"`,
        `description="${escapaAtributo(dados.description)}"`,
        `image="${escapaAtributo(dados.image)}"`,
    ];
    if (dados.duration) partes.push(`duration="${escapaAtributo(dados.duration)}"`);
    return partes.join(' ');
}

function montaAtributosPlaylistEmbed(dados) {
    const partes = [
        `id="${escapaAtributo(dados.id)}"`,
        `type="${escapaAtributo(dados.type)}"`,
        `title="${escapaAtributo(dados.title)}"`,
        `category="${escapaAtributo(dados.category)}"`,
        `image="${escapaAtributo(dados.image)}"`,
        `url="${escapaAtributo(dados.url)}"`,
    ];
    if (dados.items) partes.push(`items="${escapaAtributo(dados.items)}"`);
    return partes.join(' ');
}

function montaAtributosInstagram(dados) {
    const partes = [`url="${dados.url || ''}"`, `mode="${dados.mode || 'full'}"`];
    if (dados.mode === 'image') {
        if (dados.alt) partes.push(`alt="${dados.alt}"`);
        if (!dados.showLink) partes.push(`show-link="false"`);
    }
    if (dados.mode === 'video') {
        if (dados.videoUrl) partes.push(`video-url="${dados.videoUrl}"`);
        if (dados.controls) partes.push(`controls="true"`);
        if (dados.autoplay) partes.push(`autoplay="true"`);
        if (dados.muted) partes.push(`muted="true"`);
        if (dados.loop) partes.push(`loop="true"`);
        if (dados.preload) partes.push(`preload="${dados.preload}"`);
    }
    return partes.join(' ');
}

// Converte os blocos <pre>@@tipo@@payload</pre> gerados pelo draftToHtml em suas tags finais,
// no momento de salvar o artigo.
function converteBlocosEspeciais(html) {
    return html.replace(/<pre>([\s\S]*?)<\/pre>/g, (match, texto) => {
        const partes = texto.split('@@');
        const tipo = partes[1];

        if (tipo === 'carrossel') {
            let imagens = [];
            try { imagens = JSON.parse(decodeURIComponent(partes[2])); } catch (e) { imagens = []; }
            const imgsHtml = imagens.map(img => `<img src="${img.src}" alt="${img.alt || ''}" />`).join('');
            return `<carrousel>${imgsHtml}</carrousel>`;
        }

        if (tipo === 'instagram') {
            let dados = {};
            try { dados = JSON.parse(decodeURIComponent(partes[2])); } catch (e) { dados = {}; }
            return `<instagram ${montaAtributosInstagram(dados)}></instagram>`;
        }

        if (tipo === 'video-embed' || tipo === 'podcast-embed') {
            let dados = {};
            try { dados = JSON.parse(decodeURIComponent(partes[2])); } catch (e) { dados = {}; }
            return `<${tipo} ${montaAtributosEmbed(dados)}></${tipo}>`;
        }

        if (tipo === 'playlist') {
            let dados = {};
            try { dados = JSON.parse(decodeURIComponent(partes[2])); } catch (e) { dados = {}; }
            return `<playlist ${montaAtributosPlaylistEmbed(dados)}></playlist>`;
        }

        if (partes.length > 3) {
            return `<video data-video="@@${partes[1]}@@${partes[2]}@@${partes[3]}" data-end="#"></video>`;
        }

        if (removeEspacosHTML(texto).trim() !== '') return `<destaque><p>${texto}</p></destaque>`;

        return match;
    });
}

function reconverteEmbed(html, tagEmbed) {
    const regexEmbed = new RegExp(`<${tagEmbed}\\b([^>]*)>\\s*</${tagEmbed}>`, 'g');
    return html.replace(regexEmbed, (match, attrsStr) => {
        const attrs = extraiAtributos(`<${tagEmbed} ${attrsStr}>`);
        const dados = {
            url: desescapaAtributo(attrs.url),
            title: desescapaAtributo(attrs.title),
            program: desescapaAtributo(attrs.program),
            description: desescapaAtributo(attrs.description),
            image: desescapaAtributo(attrs.image),
            duration: attrs.duration || '',
        };
        return `<pre>@@${tagEmbed}@@${encodeURIComponent(JSON.stringify(dados))}</pre>`;
    });
}

// Caminho inverso de converteBlocosEspeciais: ao carregar um artigo existente, reescreve as tags
// finais como <pre>@@tipo@@payload</pre> para o blockRendererFn reconhecer o bloco.
function reconverteBlocosEspeciais(html) {
    let _html = reconverteEmbed(html, 'video-embed');
    _html = reconverteEmbed(_html, 'podcast-embed');

    _html = _html.replace(/<playlist\b([^>]*)>\s*<\/playlist>/g, (match, attrsStr) => {
        const attrs = extraiAtributos(`<playlist ${attrsStr}>`);
        const dados = {
            id: attrs.id || '',
            type: attrs.type || '',
            title: desescapaAtributo(attrs.title),
            category: desescapaAtributo(attrs.category),
            image: desescapaAtributo(attrs.image),
            items: attrs.items || '',
            url: desescapaAtributo(attrs.url),
        };
        return `<pre>@@playlist@@${encodeURIComponent(JSON.stringify(dados))}</pre>`;
    });

    _html = _html.replace(/<carrousel>([\s\S]*?)<\/carrousel>/g, (match, inner) => {
        const imagens = [];
        const imgTagRegex = /<img\b[^>]*>/g;
        let imgMatch;
        while ((imgMatch = imgTagRegex.exec(inner)) !== null) {
            const attrs = extraiAtributos(imgMatch[0]);
            imagens.push({ src: attrs.src || '', alt: attrs.alt || '' });
        }
        return `<pre>@@carrossel@@${encodeURIComponent(JSON.stringify(imagens))}</pre>`;
    });

    _html = _html.replace(/<instagram\b([^>]*)>\s*<\/instagram>/g, (match, attrsStr) => {
        const attrs = extraiAtributos(`<instagram ${attrsStr}>`);
        const dados = {
            url: attrs.url || '',
            mode: attrs.mode || 'full',
            alt: attrs.alt || '',
            showLink: attrs['show-link'] !== 'false',
            videoUrl: attrs['video-url'] || '',
            autoplay: attrs.autoplay === 'true',
            muted: attrs.muted === 'true',
            loop: attrs.loop === 'true',
            controls: attrs.controls !== 'false',
            preload: attrs.preload || 'metadata',
        };
        return `<pre>@@instagram@@${encodeURIComponent(JSON.stringify(dados))}</pre>`;
    });

    _html = _html.replace(/<destaque\b[^>]*>([\s\S]*?)<\/destaque>/g, (match, inner) => {
        const paragrafos = [];
        const paragrafoRegex = /<p\b[^>]*>([\s\S]*?)<\/p>/g;
        let paragrafoMatch;
        while ((paragrafoMatch = paragrafoRegex.exec(inner)) !== null) {
            paragrafos.push(`<pre>${paragrafoMatch[1].replace(/\s+/g, ' ').trim()}</pre>`);
        }
        if (!paragrafos.length) return `<pre>${inner.replace(/\s+/g, ' ').trim()}</pre>`;
        return paragrafos.join('');
    });

    return _html;
}

// Artigos salvos antes da tag <video-embed> guardam o vídeo como
// <video data-video="@@idPrograma@@idEpisodio@@nome" data-end="#"></video>.
function reconverteVideoLegado(html) {
    return html.replace(/<video(?!-)[^>]*>[\s\S]*?<\/video>/g, match => {
        const partes = match.split('@@');
        if (partes.length < 4) return match;
        const nome = partes[3].split('"')[0].split('<')[0];
        return `<pre>@@${partes[1]}@@${partes[2]}@@${nome}</pre>`;
    });
}

export function htmlParaEditorState(html) {
    if (!html || removeEspacosHTML(html) === '') return EditorState.createEmpty();
    let textoTratado = reconverteVideoLegado(html);
    textoTratado = reconverteBlocosEspeciais(textoTratado);
    const blocksFromHtml = htmlToDraft(textoTratado);
    if (!blocksFromHtml || !blocksFromHtml.contentBlocks) return EditorState.createEmpty();
    const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap);
    return EditorState.createWithContent(contentState);
}

export function editorStateParaHtml(editorState) {
    let html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    html = converteBlocosEspeciais(html);
    html = replaceAll(html, `style="float:none;height: ;width: "/>`, `style="float:none;margin:0 auto;display:block; width: 100%;"/>`);
    html = replaceAll(html, `style="float:center;height: ;width: "/>`, `style="float:none;margin:0 auto;display:block; width: 100%;"/>`);
    html = replaceAll(html, `style="float:left;height: ;width: "/>`, `style="float:none;margin:0 auto;display:block; width: 100%;"/>`);
    html = replaceAll(html, `style="float:right;height: ;width: "/>`, `style="float:none;margin:0 auto;display:block; width: 100%;"/>`);
    html = replaceAll(html, `frameBorder="0"></iframe>`, `mozallowfullscreen webkitallowfullscreen allowfullscreen frameBorder="0"></iframe>`);
    return html;
}

function duracaoFormatada(duracao) {
    if (!duracao) return '';
    const _minutos = Math.floor(duracao / 60);
    const _segundos = duracao - _minutos * 60;
    return `${_minutos}:${_segundos < 10 ? '0' : ''}${_segundos}`;
}

function TagPlayerVideo({ blockProps }) {
    const { imagem, titulo, programa, duracao, icone } = blockProps;
    const _legenda = [programa, duracaoFormatada(duracao)].filter(item => !!item).join(' • ');
    return (
        <div contentEditable={false} className="div-video-player" style={{ height: 'auto' }}>
            <section contentEditable={false}>
                <div className="row no-gutters align-items-center">
                    {!!imagem && (
                        <div className="col-auto pr-3">
                            <img src={imagem} alt={titulo} style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }} />
                        </div>
                    )}
                    <div className="col text-left">
                        <p className="m-0">
                            <i className={icone || "fas fa-play-circle"}></i> {titulo}
                        </p>
                        {!!_legenda && <p className="m-0 font-weight-normal"><small>{_legenda}</small></p>}
                    </div>
                </div>
            </section>
        </div>
    );
}

// Diferente das tags de mídia, o destaque envolve texto do editorial que continua editável: o
// EditorBlock mantém o texto do bloco sob controle do Draft e a div só aplica o estilo.
function TagDestaque(props) {
    return (
        <div className="bloco-destaque">
            <EditorBlock {...props} />
        </div>
    );
}

function TagPlaylist({ blockProps }) {
    const { imagem, titulo, tipo, itens } = blockProps;
    const _legenda = [tipo === 'podcast' ? 'Podcasts' : 'Vídeos', !!itens ? `${itens} itens` : ''].filter(item => !!item).join(' • ');
    return (
        <div contentEditable={false} className="div-video-player" style={{ height: 'auto' }}>
            <section contentEditable={false}>
                <div className="row no-gutters align-items-center">
                    {!!imagem && (
                        <div className="col-auto pr-3">
                            <img src={imagem} alt={titulo} style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }} />
                        </div>
                    )}
                    <div className="col text-left">
                        <p className="m-0">
                            <i className="fas fa-list"></i> {titulo}
                        </p>
                        <p className="m-0 font-weight-normal"><small>{_legenda}</small></p>
                    </div>
                </div>
            </section>
        </div>
    );
}

function TagCarrossel({ blockProps }) {
    const { imagens, blockKey, onEditar } = blockProps;
    const lista = imagens || [];
    return (
        <div contentEditable={false} className="div-video-player" style={{ height: 'auto' }}>
            <section contentEditable={false}>
                <div className="row no-gutters align-items-center">
                    <div className="col">
                        <p className="m-0">
                            <i className="fas fa-images"></i> Carrossel de imagens ({lista.length})
                        </p>
                    </div>
                    <div className="col-auto">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => { e.stopPropagation(); onEditar(blockKey, lista); }}
                        >
                            editar
                        </button>
                    </div>
                </div>
                <div className="row no-gutters justify-content-center">
                    {lista.map((img, index) => (
                        <img key={`carrossel-thumb-${index}`} src={img.src} alt={img.alt} style={{ height: 60, margin: 4 }} />
                    ))}
                </div>
            </section>
        </div>
    );
}

function TagInstagram({ blockProps }) {
    const dados = blockProps.dados || {};
    const _modo = modosInstagram.find(item => item.value === dados.mode);
    const _legenda = !!_modo ? _modo.curto : dados.mode;
    return (
        <div contentEditable={false} className="div-video-player">
            <section contentEditable={false}>
                <p className="m-0">
                    <i className="fab fa-instagram"></i> Instagram ({_legenda}): {dados.url}
                </p>
            </section>
        </div>
    );
}

const toolbar = {
    inline: {
        inDropdown: false,
        options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
    },
    blockType: {
        inDropdown: true,
        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
    },
    fontSize: {
        options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
    },
    fontFamily: {
        options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
    },
    list: {
        inDropdown: false,
        options: ['unordered', 'ordered', 'indent', 'outdent'],
    },
    textAlign: {
        inDropdown: false,
        options: ['left', 'center', 'right', 'justify'],
    },
    colorPicker: {
        colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
            'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
            'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
            'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
            'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
            'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)',
            'rgb(0,185,242)', 'rgb(0,162,224)', 'rgb(0,74,143)', 'rgb(0,132,182)', 'rgb(0,174,197)',
            'rgb(0,155,144)', 'rgb(114,191,68)', 'rgb(191,215,48)', 'rgb(249,157,28)', 'rgb(244,121,32)',
            'rgb(239,65,61)', 'rgb(237,9,115)', 'rgb(240,78,105)', 'rgb(242,135,183)', 'rgb(169,103,170)',
            'rgb(171,33,142)', 'rgb(100,47,136)'],
    },
    link: {
        inDropdown: false,
        showOpenOptionOnHover: true,
        defaultTargetOption: '_self',
        options: ['link', 'unlink'],
    },
    emoji: {
        emojis: [
            '😀', '😁', '😂', '😃', '😉', '😋', '😎', '😍', '😗', '🤗', '🤔', '😣', '😫', '😴', '😌', '🤓',
            '😛', '😜', '😠', '😇', '😷', '😈', '👻', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '🙈',
            '🙉', '🙊', '👼', '👮', '🕵', '💂', '👳', '🎅', '👸', '👰', '👲', '🙍', '🙇', '🚶', '🏃', '💃',
            '⛷', '🏂', '🏌', '🏄', '🚣', '🏊', '⛹', '🏋', '🚴', '👫', '💪', '👈', '👉', '👉', '👆', '🖕',
            '👇', '🖖', '🤘', '🖐', '👌', '👍', '👎', '✊', '👊', '👏', '🙌', '🙏', '🐵', '🐶', '🐇', '🐥',
            '🐸', '🐌', '🐛', '🐜', '🐝', '🍉', '🍄', '🍔', '🍤', '🍨', '🍪', '🎂', '🍰', '🍾', '🍷', '🍸',
            '🍺', '🌍', '🚑', '⏰', '🌙', '🌝', '🌞', '⭐', '🌟', '🌠', '🌨', '🌩', '⛄', '🔥', '🎄', '🎈',
            '🎉', '🎊', '🎁', '🎗', '🏀', '🏈', '🎲', '🔇', '🔈', '📣', '🔔', '🎵', '🎷', '💰', '🖊', '📅',
            '✅', '❎', '💯',
        ],
    },
    embedded: {
        defaultSize: { height: 'auto', width: 'auto' },
    },
    image: {
        className: 'd-none',
        urlEnabled: true,
        uploadEnabled: true,
        alignmentEnabled: true,
        previewImage: false,
        inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
        alt: { present: false, mandatory: false },
        defaultSize: { height: 'auto', width: 'auto' },
    },
    remove: {},
    history: {
        inDropdown: false,
        options: ['undo', 'redo'],
    },
};

const ArticleTextEditor = ({ initialValue, onChange, disabled }) => {
    const [editorState, setEditorState] = useState(() => htmlParaEditorState(initialValue));

    const [openModalImagemTexto, setOpenModalImagemTexto] = useState(false);
    const [openModalVideo, setOpenModalVideo] = useState(false);
    const [openModalPodcast, setOpenModalPodcast] = useState(false);
    const [openModalPlaylist, setOpenModalPlaylist] = useState(false);
    const [openModalCarrossel, setOpenModalCarrossel] = useState(false);
    const [openModalInstagram, setOpenModalInstagram] = useState(false);
    const [carrosselEmEdicaoBlockKey, setCarrosselEmEdicaoBlockKey] = useState(null);
    const [carrosselEmEdicaoImagens, setCarrosselEmEdicaoImagens] = useState(null);

    const atualizaEditorState = (novoEditorState) => {
        setEditorState(novoEditorState);
        onChange(editorStateParaHtml(novoEditorState));
    };

    const insertImage = (src, alt) => {
        const entityData = { src, height: '', width: '', alt };
        const entityKey = editorState
            .getCurrentContent()
            .createEntity('IMAGE', 'MUTABLE', entityData)
            .getLastCreatedEntityKey();
        const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
        atualizaEditorState(newEditorState);
    };

    const onChangeInputImagem = (e) => {
        if (e.target.name === 'imagem-texto') insertImage(e.target.value, e.target.alt);
    };

    const applySmallTag = () => atualizaEditorState(RichUtils.toggleInlineStyle(editorState, 'SUBSCRIPT'));
    const applyDestaqueTag = () => atualizaEditorState(RichUtils.toggleBlockType(editorState, 'code'));

    // Insere um novo bloco tipo 'code', mesmo mecanismo usado pelos embeds de vídeo/podcast/
    // carrossel/instagram/playlist.
    const inserirBlocoCode = (texto) => {
        const selectionState = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const currentBlock = contentState.getBlockForKey(selectionState.getStartKey());
        const currentBlockKey = currentBlock.getKey();
        const blockMap = contentState.getBlockMap();
        const blocksBefore = blockMap.toSeq().takeUntil((v) => (v === currentBlock));
        const blocksAfter = blockMap.toSeq().skipUntil((v) => (v === currentBlock)).rest();
        const newBlockKey = genKey();

        const newBlock = new ContentBlock({
            key: newBlockKey,
            type: 'code',
            text: texto,
            characterList: new List(Repeat(CharacterMetadata.create(), texto.length)),
        });

        const newBlockMap = blocksBefore.concat(
            [[currentBlockKey, currentBlock], [newBlockKey, newBlock]],
            blocksAfter
        ).toOrderedMap();

        const selection = editorState.getSelection();

        const newContent = contentState.merge({
            blockMap: newBlockMap,
            selectionBefore: selection,
            selectionAfter: selection.merge({
                anchorKey: newBlockKey,
                anchorOffset: 0,
                focusKey: newBlockKey,
                focusOffset: 0,
                isBackward: false,
            }),
        });

        let newEditorState = EditorState.push(editorState, newContent, 'split-block');

        const newSelection = new SelectionState({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: texto.length,
        });

        newEditorState = EditorState.forceSelection(newEditorState, newSelection);
        atualizaEditorState(newEditorState);
    };

    const substituiBlocoCode = (blockKey, texto) => {
        const contentState = editorState.getCurrentContent();
        const blockAntigo = contentState.getBlockForKey(blockKey);

        const blockAtualizado = new ContentBlock({
            key: blockKey,
            type: blockAntigo.getType(),
            text: texto,
            characterList: new List(Repeat(CharacterMetadata.create(), texto.length)),
        });

        const newBlockMap = contentState.getBlockMap().set(blockKey, blockAtualizado);
        const newContent = contentState.merge({ blockMap: newBlockMap });
        const newEditorState = EditorState.push(editorState, newContent, 'change-block-data');
        atualizaEditorState(newEditorState);
    };

    const handleInsertVideo = ({ idProg, idEp, nomeEp, episodio, nomePrograma }) => {
        const _episodio = episodio || { idVideoPrograma: idProg, idVideoEpisodio: idEp, nome: nomeEp };
        const inserirVideoEmbed = (_ep, _nomePrograma) => {
            const _dados = {
                url: ['https://brasilseikyo.com.br/videos/programas', _ep.idVideoPrograma, 'episodio', stringToSlug(_nomePrograma || ''), _ep.idVideoEpisodio].join('/'),
                title: _ep.nome || '',
                program: _nomePrograma || '',
                description: _ep.descricao || '',
                image: _ep.imagem || '',
                duration: _ep.duracao || '',
            };
            inserirBlocoCode(`@@video-embed@@${encodeURIComponent(JSON.stringify(_dados))}`);
        };

        if (_episodio.duracao === undefined || _episodio.duracao === null) {
            const config = new Configurations();
            axios.get(config.apiBasePath + `/videos/${idProg}/episodios/${idEp}`)
                .then(response => inserirVideoEmbed(response.data.videosEpisodios[0], nomePrograma))
                .catch(() => inserirVideoEmbed(_episodio, nomePrograma));
        } else inserirVideoEmbed(_episodio, nomePrograma);
    };

    const handleModalVideo = (dados) => {
        setOpenModalVideo(false);
        handleInsertVideo(dados);
    };

    const handleInsertPodcast = ({ idProg, idEpisodio, nomeEpisodio, episodio, nomePrograma }) => {
        const _episodio = episodio || { nome: nomeEpisodio };
        const inserirPodcastEmbed = (_ep, _nomePrograma) => {
            const _dados = {
                url: ['https://brasilseikyo.com.br/podcast/programa', idProg, 'episodio', stringToSlug(_nomePrograma || ''), idEpisodio].join('/'),
                title: _ep.nome || '',
                program: _nomePrograma || '',
                description: _ep.descricao || '',
                image: _ep.imagem || '',
                duration: _ep.duracao || '',
            };
            inserirBlocoCode(`@@podcast-embed@@${encodeURIComponent(JSON.stringify(_dados))}`);
        };

        if (_episodio.duracao === undefined || _episodio.duracao === null) {
            const config = new Configurations();
            axios.get(config.apiBasePath + `/podcasts/${idProg}/episodios/${idEpisodio}`)
                .then(response => inserirPodcastEmbed(response.data.data, nomePrograma))
                .catch(() => inserirPodcastEmbed(_episodio, nomePrograma));
        } else inserirPodcastEmbed(_episodio, nomePrograma);
    };

    const handleModalPodcast = (dados) => {
        setOpenModalPodcast(false);
        handleInsertPodcast(dados);
    };

    const handleModalPlaylist = ({ tipo, playlist }) => {
        setOpenModalPlaylist(false);
        const _playlist = playlist || {};
        const _dados = {
            id: _playlist.id || '',
            type: tipo,
            title: _playlist.nomePlaylist || '',
            category: _playlist.categoria || '',
            image: _playlist.image || '',
            items: _playlist.quantidadeItens || '',
            url: `https://www.brasilseikyo.com.br/${tipo}/playlists/${_playlist.id}/${stringToSlug(_playlist.nomePlaylist)}`,
            duration: _playlist.totalDuracao || '',
        };
        inserirBlocoCode(`@@playlist@@${encodeURIComponent(JSON.stringify(_dados))}`);
    };

    const iniciarEdicaoCarrossel = (blockKey, imagens) => {
        setCarrosselEmEdicaoBlockKey(blockKey);
        setCarrosselEmEdicaoImagens(imagens);
        setOpenModalCarrossel(true);
    };

    const toggleModalCarrossel = () => {
        setOpenModalCarrossel(!openModalCarrossel);
        if (openModalCarrossel) {
            setCarrosselEmEdicaoBlockKey(null);
            setCarrosselEmEdicaoImagens(null);
        }
    };

    const handleModalCarrossel = (imagens) => {
        setOpenModalCarrossel(false);
        const texto = `@@carrossel@@${encodeURIComponent(JSON.stringify(imagens))}`;
        if (carrosselEmEdicaoBlockKey) {
            substituiBlocoCode(carrosselEmEdicaoBlockKey, texto);
        } else {
            inserirBlocoCode(texto);
        }
        setCarrosselEmEdicaoBlockKey(null);
        setCarrosselEmEdicaoImagens(null);
    };

    const handleModalInstagram = (dados) => {
        setOpenModalInstagram(false);
        inserirBlocoCode(`@@instagram@@${encodeURIComponent(JSON.stringify(dados))}`);
    };

    const blockRendererFn = (contentBlock) => {
        const type = contentBlock.getType();
        if (type !== 'code') return;

        const _texto = contentBlock.getText();
        const _partes = _texto.split('@@');
        const _tipo = _partes[1];

        if (_tipo === 'carrossel') {
            let _imagens = [];
            try { _imagens = JSON.parse(decodeURIComponent(_partes[2])); } catch (e) { _imagens = []; }
            return { component: TagCarrossel, editable: false, props: { imagens: _imagens, blockKey: contentBlock.getKey(), onEditar: iniciarEdicaoCarrossel } };
        }

        if (_tipo === 'instagram') {
            let _dados = {};
            try { _dados = JSON.parse(decodeURIComponent(_partes[2])); } catch (e) { _dados = {}; }
            return { component: TagInstagram, editable: false, props: { dados: _dados } };
        }

        if (_tipo === 'video-embed') {
            let _dados = {};
            try { _dados = JSON.parse(decodeURIComponent(_partes[2])); } catch (e) { _dados = {}; }
            return { component: TagPlayerVideo, editable: false, props: { imagem: _dados.image, titulo: _dados.title, programa: _dados.program, duracao: _dados.duration } };
        }

        if (_tipo === 'podcast-embed') {
            let _dados = {};
            try { _dados = JSON.parse(decodeURIComponent(_partes[2])); } catch (e) { _dados = {}; }
            return { component: TagPlayerVideo, editable: false, props: { imagem: _dados.image, titulo: _dados.title, programa: _dados.program, duracao: _dados.duration, icone: 'fas fa-podcast' } };
        }

        if (_tipo === 'playlist') {
            let _dados = {};
            try { _dados = JSON.parse(decodeURIComponent(_partes[2])); } catch (e) { _dados = {}; }
            return { component: TagPlaylist, editable: false, props: { imagem: _dados.image, titulo: _dados.title, tipo: _dados.type, itens: _dados.items } };
        }

        const _infosValidas = _partes.length > 3;
        if (_infosValidas) {
            return { component: TagPlayerVideo, editable: false, props: { titulo: _partes[3] } };
        }

        return { component: TagDestaque, editable: true };
    };

    const imgToolbar = (
        <div onClick={() => setOpenModalImagemTexto(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper" title="Imagem">
                <i className="fas fa-image"></i>
            </div>
        </div>
    );
    const tagSmallToolbar = (
        <div onClick={applySmallTag} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper" title="Small"><em>Legenda</em></div>
        </div>
    );
    const destaqueToolbar = (
        <div onClick={applyDestaqueTag} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper" title="Destaque"><strong>Destaque</strong></div>
        </div>
    );
    const videoToolbar = (
        <div onClick={() => setOpenModalVideo(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper video" title="Vídeo"><i className="fas fas fa-film"></i></div>
        </div>
    );
    const podcastToolbar = (
        <div onClick={() => setOpenModalPodcast(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper video" title="Podcast"><i className="fas fas fa-podcast"></i></div>
        </div>
    );
    const playlistToolbar = (
        <div onClick={() => setOpenModalPlaylist(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper video" title="Playlist"><i className="fas fa-list"></i></div>
        </div>
    );
    const carrosselToolbar = (
        <div onClick={() => setOpenModalCarrossel(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper video" title="Carrossel de imagens"><i className="fas fa-images"></i></div>
        </div>
    );
    const instagramToolbar = (
        <div onClick={() => setOpenModalInstagram(true)} className="rdw-image-wrapper" aria-haspopup="true" aria-expanded="false" aria-label="rdw-image-control">
            <div className="rdw-option-wrapper video" title="Instagram"><i className="fab fa-instagram"></i></div>
        </div>
    );

    return (
        <div className="article-text-editor">
            <Editor
                readOnly={disabled}
                editorState={editorState}
                wrapperClassName="demo-wrapper"
                onEditorStateChange={atualizaEditorState}
                toolbar={toolbar}
                toolbarCustomButtons={[
                    imgToolbar, videoToolbar, podcastToolbar, playlistToolbar,
                    carrosselToolbar, instagramToolbar, tagSmallToolbar, destaqueToolbar,
                ]}
                toolbarClassName={`custom-toolbar ${disabled ? 'd-none' : ''}`}
                editorClassName="custom-editor-texto border px-3"
                handlePastedText={() => false}
                customBlockRenderFunc={blockRendererFn}
            />
            <div className="row mt-1">
                <div className="col-6">
                    <p className="small text-muted m-0">Palavras: {contadorTexto(draftToHtml(convertToRaw(editorState.getCurrentContent()))).palavras}</p>
                </div>
                <div className="col-6 text-right">
                    <p className="small text-muted m-0">Caracteres: {contadorTexto(draftToHtml(convertToRaw(editorState.getCurrentContent()))).caracteres}</p>
                </div>
            </div>

            <Modal className="modal-upload-imagem" size="lg" isOpen={openModalImagemTexto} toggle={() => setOpenModalImagemTexto(false)} returnFocusAfterClose={false}>
                <ModalHeader toggle={() => setOpenModalImagemTexto(false)}>Upload de imagem</ModalHeader>
                <ModalBody>
                    <ImagesUpload
                        toggle={() => setOpenModalImagemTexto(false)}
                        onChangeInput={onChangeInputImagem}
                        imgNoTexto={true}
                        alteracao={true}
                    />
                </ModalBody>
            </Modal>

            <InsertVideo isOpen={openModalVideo} toggle={() => setOpenModalVideo(false)} handleModalVideo={handleModalVideo} />
            <InsertPodcast isOpen={openModalPodcast} toggle={() => setOpenModalPodcast(false)} handleModalPodcast={handleModalPodcast} />
            <InsertPlaylist isOpen={openModalPlaylist} toggle={() => setOpenModalPlaylist(false)} handleModalPlaylist={handleModalPlaylist} />
            <InsertCarrossel
                isOpen={openModalCarrossel}
                toggle={toggleModalCarrossel}
                handleModalCarrossel={handleModalCarrossel}
                imagensIniciais={carrosselEmEdicaoImagens}
                editando={!!carrosselEmEdicaoBlockKey}
            />
            <InsertInstagram isOpen={openModalInstagram} toggle={() => setOpenModalInstagram(false)} handleModalInstagram={handleModalInstagram} />
        </div>
    );
};

export default ArticleTextEditor;
